// src/pages/SubstituteApprovals.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc,
  serverTimestamp,
  orderBy 
} from 'firebase/firestore';

const SubstituteApprovals = ({ currentUser }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadRegistrations();
      setDebugInfo(`User: ${currentUser.email}, Role: ${currentUser.role}, TenantId: ${currentUser.tenantId}`);
    }
  }, [currentUser, filter]);

  // Fields to exclude from display (system metadata)
  const excludedFields = [
    'id', 'tenantId', 'status', 'submittedAt', 'reviewedAt', 
    'reviewedBy', 'reviewedByEmail', 'userAgent', 'ipAddress', 
    'registrationType', 'registrationId', 'createdBy', 'notes'
  ];

  // Format field names for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1)); // Title case
  };

  // Format field values for display
  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>Not provided</span>;
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : <span style={{ color: '#999', fontStyle: 'italic' }}>None specified</span>;
    }
    
    if (typeof value === 'object' && value.toDate) {
      // Firebase timestamp
      return formatDate(value);
    }
    
    if (typeof value === 'object') {
      // Complex object - render as key-value pairs
      return (
        <div style={{ fontSize: '13px' }}>
          {Object.entries(value).map(([key, val]) => (
            <div key={key} style={{ marginBottom: '2px' }}>
              <strong>{formatFieldName(key)}:</strong> {formatFieldValue(val)}
            </div>
          ))}
        </div>
      );
    }
    
    return String(value);
  };

  // Get all fields from a registration, excluding system fields
  const getDisplayFields = (registration) => {
    return Object.keys(registration)
      .filter(key => !excludedFields.includes(key))
      .sort(); // Alphabetical order
  };

  // Get key contact info for the summary view
  const getContactSummary = (registration) => {
    const name = `${registration.firstName || 'Unknown'} ${registration.lastName || 'User'}`;
    const email = registration.email || 'No email provided';
    const phone = registration.phone || 'No phone provided';
    return { name, email, phone };
  };

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading substitute registrations for tenant:', currentUser.tenantId);
      
      let q;
      if (filter === 'all') {
        q = query(
          collection(db, 'substituteRegistrations'),
          where('tenantId', '==', currentUser.tenantId),
          orderBy('submittedAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'substituteRegistrations'),
          where('tenantId', '==', currentUser.tenantId),
          where('status', '==', filter),
          orderBy('submittedAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      const registrationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Loaded substitute registrations:', registrationsData);
      setRegistrations(registrationsData);
      
    } catch (err) {
      console.error('Error loading substitute registrations:', err);
      setError(`Failed to load substitute registrations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (registrationId, status, notes = '') => {
    try {
      setProcessing(prev => ({ ...prev, [registrationId]: true }));
      setError('');
      
      const registration = registrations.find(r => r.id === registrationId);
      if (!registration) {
        throw new Error('Registration not found');
      }
      
      // Update registration status
      const updateData = {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid,
        reviewedByEmail: currentUser.email,
        notes: notes || ''
      };
      
      const registrationRef = doc(db, 'substituteRegistrations', registrationId);
      await updateDoc(registrationRef, updateData);

      // If approved, create substitute profile in faculty_staff collection
      if (status === 'approved') {
        // Create faculty_staff record with ALL the registration data (excluding system fields)
        const facultyData = {
          // Standard fields
          firstName: registration.firstName || 'Unknown',
          lastName: registration.lastName || 'User',
          name: `${registration.firstName || 'Unknown'} ${registration.lastName || 'User'}`,
          email: registration.email || '',
          role: 'Outside Sub',
          department: 'Substitute',
          tenantId: currentUser.tenantId,
          status: 'active',
          joinedAt: serverTimestamp(),
          registrationId: registrationId,
          createdBy: currentUser.email,
          substituteType: 'outside'
        };

        // Add all non-system fields from registration
        const displayFields = getDisplayFields(registration);
        displayFields.forEach(field => {
          if (!facultyData.hasOwnProperty(field)) {
            facultyData[field] = registration[field];
          }
        });

        await addDoc(collection(db, 'faculty_staff'), facultyData);
        console.log('Substitute profile created successfully in faculty_staff');
      }

      await loadRegistrations();
      setSelectedRegistration(null);
      setBulkSelected(prev => prev.filter(id => id !== registrationId));
      
    } catch (err) {
      console.error('Error processing substitute approval:', err);
      setError(`Failed to process registration: ${err.message}`);
    } finally {
      setProcessing(prev => ({ ...prev, [registrationId]: false }));
    }
  };

  const handleBulkAction = async (action) => {
    if (bulkSelected.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${bulkSelected.length} substitute registration(s)?`
    );

    if (!confirmed) return;

    for (const registrationId of bulkSelected) {
      await handleApproval(registrationId, action);
    }

    setBulkSelected([]);
  };

  const toggleBulkSelection = (registrationId) => {
    setBulkSelected(prev => 
      prev.includes(registrationId)
        ? prev.filter(id => id !== registrationId)
        : [...prev, registrationId]
    );
  };

  const selectAllVisible = () => {
    const pendingIds = registrations
      .filter(reg => filter === 'all' || reg.status === filter)
      .map(reg => reg.id);
    setBulkSelected(pendingIds);
  };

  const clearSelection = () => {
    setBulkSelected([]);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'approved': return '#28a745';
      case 'denied': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const filteredRegistrations = registrations.filter(reg => 
    filter === 'all' || reg.status === filter
  );

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading substitute registrations...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
          Substitute Teacher Registration Review
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
          Review and approve substitute teacher applications
        </p>
        
        {/* Debug Info */}
        <div style={{ 
          background: '#f0f0f0', 
          padding: '10px', 
          marginTop: '15px', 
          fontSize: '12px',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}>
          <strong>Debug Info:</strong> {debugInfo}
        </div>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filters and Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontWeight: 'bold', color: '#333' }}>Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="pending">Pending ({registrations.filter(r => r.status === 'pending').length})</option>
            <option value="approved">Approved ({registrations.filter(r => r.status === 'approved').length})</option>
            <option value="denied">Denied ({registrations.filter(r => r.status === 'denied').length})</option>
            <option value="all">All ({registrations.length})</option>
          </select>
          
          <button 
            onClick={loadRegistrations}
            style={{ 
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: '#f8f9fa',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>

        {bulkSelected.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>
              {bulkSelected.length} selected
            </span>
            <button
              onClick={() => handleBulkAction('approved')}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Approve Selected
            </button>
            <button
              onClick={() => handleBulkAction('denied')}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Deny Selected
            </button>
            <button
              onClick={clearSelection}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {filteredRegistrations.length > 0 && filter === 'pending' && (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={selectAllVisible}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            Select All Visible
          </button>
        </div>
      )}

      {/* Registrations List */}
      {filteredRegistrations.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: 'white', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            No {filter !== 'all' ? filter : ''} substitute registrations found
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            {filter === 'pending' 
              ? 'All substitute applications have been reviewed.'
              : 'Try changing the filter to see other registrations.'
            }
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredRegistrations.map(registration => {
            const contactSummary = getContactSummary(registration);
            const displayFields = getDisplayFields(registration);
            
            return (
              <div key={registration.id} style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6',
                overflow: 'hidden',
                cursor: 'pointer'
              }}
              onClick={() => setSelectedRegistration(
                selectedRegistration === registration.id ? null : registration.id
              )}>
                <div style={{ 
                  padding: '20px',
                  borderBottom: selectedRegistration === registration.id ? '1px solid #dee2e6' : 'none'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        {filter === 'pending' && (
                          <input
                            type="checkbox"
                            checked={bulkSelected.includes(registration.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleBulkSelection(registration.id);
                            }}
                            style={{ marginRight: '12px' }}
                          />
                        )}
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
                          {contactSummary.name}
                        </h3>
                        <span style={{
                          marginLeft: '12px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          backgroundColor: getStatusColor(registration.status),
                          color: 'white',
                          textTransform: 'capitalize'
                        }}>
                          {registration.status}
                        </span>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <strong>Contact:</strong><br />
                          {contactSummary.email}<br />
                          {contactSummary.phone}
                        </div>
                        <div>
                          <strong>Submitted:</strong><br />
                          {formatDate(registration.submittedAt)}
                        </div>
                        {registration.reviewedAt && (
                          <div>
                            <strong>Reviewed:</strong><br />
                            {formatDate(registration.reviewedAt)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRegistration(
                            selectedRegistration === registration.id ? null : registration.id
                          );
                        }}
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        {selectedRegistration === registration.id ? 'Hide Details' : 'View Details'}
                      </button>

                      {registration.status === 'pending' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproval(registration.id, 'approved');
                            }}
                            disabled={processing[registration.id]}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: processing[registration.id] ? 'not-allowed' : 'pointer',
                              opacity: processing[registration.id] ? 0.6 : 1
                            }}
                          >
                            {processing[registration.id] ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproval(registration.id, 'denied');
                            }}
                            disabled={processing[registration.id]}
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: processing[registration.id] ? 'not-allowed' : 'pointer',
                              opacity: processing[registration.id] ? 0.6 : 1
                            }}
                          >
                            {processing[registration.id] ? 'Processing...' : 'Deny'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Dynamic Expanded Details */}
                  {selectedRegistration === registration.id && (
                    <div style={{ 
                      borderTop: '1px solid #dee2e6', 
                      paddingTop: '16px', 
                      marginTop: '16px',
                      fontSize: '14px'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>
                        Complete Application Details
                      </h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                        {displayFields.map(fieldKey => (
                          <div key={fieldKey} style={{ marginBottom: '12px' }}>
                            <strong style={{ color: '#333' }}>{formatFieldName(fieldKey)}:</strong>
                            <div style={{ marginTop: '4px' }}>
                              {formatFieldValue(registration[fieldKey])}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Admin Notes */}
                      {registration.notes && (
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #dee2e6' }}>
                          <strong style={{ color: '#333' }}>Review Notes:</strong>
                          <div style={{ 
                            padding: '8px', 
                            backgroundColor: '#fff3cd', 
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#856404',
                            marginTop: '4px'
                          }}>
                            {registration.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Click to expand hint */}
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  color: '#999', 
                  padding: '8px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  {selectedRegistration === registration.id ? 'Click to collapse details' : 'Click to view full details'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubstituteApprovals;